import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  db,
  functions,
  collection,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  getDocs,
} from '../firebase';
import { httpsCallable } from 'firebase/functions';

const EMAIL_DEFAULTS = {
  enabled: false,
  fromEmail: 'noreply.haydi@gmail.com',
  fromName: 'Haydi',
  smtpHost: 'smtp.gmail.com',
  smtpPort: 465,
  smtpSecure: true,
  smtpUser: 'noreply.haydi@gmail.com',
  replyTo: '',
};

function AdminTab({ user }) {
  const { t } = useTranslation();
  const [view, setView] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    avatarUrl: '',
    role: 'user',
  });

  const [scoreInputs, setScoreInputs] = useState({});

  const [emailForm, setEmailForm] = useState(() => ({
    ...EMAIL_DEFAULTS,
    smtpPassword: '',
  }));
  const [emailHasStoredPassword, setEmailHasStoredPassword] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTestSending, setEmailTestSending] = useState(false);

  const [marriageForm, setMarriageForm] = useState({
    title: '',
    sinceLocal: '',
    visibleUserIds: [],
  });
  const [marriageSaving, setMarriageSaving] = useState(false);

  const [chatForm, setChatForm] = useState({ unansweredAfterHours: 24 });
  const [chatSaving, setChatSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    const us = await getDocs(collection(db, 'users'));
    setUsers(us.docs.map((d) => ({ _id: d.id, ...d.data() })));
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'email'), (snap) => {
      if (!snap.exists()) {
        setEmailForm((f) => ({ ...f, ...EMAIL_DEFAULTS, smtpPassword: '' }));
        setEmailHasStoredPassword(false);
        return;
      }
      const d = snap.data();
      setEmailHasStoredPassword(!!d.smtpPassword);
      setEmailForm((f) => ({
        ...f,
        enabled: !!d.enabled,
        fromEmail: d.fromEmail ?? EMAIL_DEFAULTS.fromEmail,
        fromName: d.fromName ?? EMAIL_DEFAULTS.fromName,
        smtpHost: d.smtpHost ?? EMAIL_DEFAULTS.smtpHost,
        smtpPort: typeof d.smtpPort === 'number' ? d.smtpPort : EMAIL_DEFAULTS.smtpPort,
        smtpSecure: d.smtpSecure !== false,
        smtpUser: d.smtpUser ?? d.fromEmail ?? EMAIL_DEFAULTS.smtpUser,
        replyTo: d.replyTo ?? '',
        smtpPassword: '',
      }));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'chat'), (snap) => {
      if (!snap.exists()) {
        setChatForm({ unansweredAfterHours: 24 });
        return;
      }
      const d = snap.data();
      const h = Number(d.unansweredAfterHours);
      setChatForm({
        unansweredAfterHours: Number.isFinite(h) ? Math.min(168, Math.max(1, h)) : 24,
      });
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'marriage'), (snap) => {
      if (!snap.exists()) {
        setMarriageForm({ title: '', sinceLocal: '', visibleUserIds: [] });
        return;
      }
      const d = snap.data();
      const dt = d.since?.toDate?.() ?? new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const local = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      setMarriageForm({
        title: d.title ?? '',
        sinceLocal: local,
        visibleUserIds: Array.isArray(d.visibleUserIds) ? [...d.visibleUserIds] : [],
      });
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr('');
      try {
        await loadUsers();
      } catch (e) {
        setErr(e.message || 'Error');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const create = httpsCallable(functions, 'adminCreateUser');
      await create(createForm);
      setCreateForm({ name: '', email: '', password: '', phone: '', avatarUrl: '', role: 'user' });
      await loadUsers();
    } catch (e2) {
      setErr(e2.message || 'Error');
    }
  };

  const toggleFreeze = async (u) => {
    try {
      const upd = httpsCallable(functions, 'adminUpdateUser');
      await upd({ uid: u._id, frozen: !u.frozen });
      await loadUsers();
    } catch (e2) {
      setErr(e2.message || 'Error');
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(t('confirmDeleteUser'))) return;
    try {
      const del = httpsCallable(functions, 'adminDeleteUser');
      await del({ uid: u._id });
      await loadUsers();
    } catch (e2) {
      setErr(e2.message || 'Error');
    }
  };

  const saveUserProfileScore = async (u) => {
    setErr('');
    try {
      const raw = scoreInputs[u._id];
      const n = raw !== undefined && String(raw).trim() !== '' ? Number(raw) : Number(u.profileScore ?? 0);
      if (!Number.isFinite(n)) throw new Error(t('adminProfileScoreInvalid'));
      const clamped = Math.max(0, Math.min(1_000_000, Math.trunc(n)));
      await updateDoc(doc(db, 'users', u._id), {
        profileScore: clamped,
        updatedAt: serverTimestamp(),
      });
      await loadUsers();
      setScoreInputs((s) => {
        const next = { ...s };
        delete next[u._id];
        return next;
      });
    } catch (e2) {
      setErr(e2.message || 'Error');
    }
  };

  const saveEmailSettings = async (e) => {
    e.preventDefault();
    setErr('');
    setEmailSaving(true);
    try {
      const ref = doc(db, 'settings', 'email');
      const prev = await getDoc(ref);
      const pwd = emailForm.smtpPassword.trim();
      const payload = {
        enabled: !!emailForm.enabled,
        fromEmail: String(emailForm.fromEmail || '').trim(),
        fromName: String(emailForm.fromName || '').trim(),
        smtpHost: String(emailForm.smtpHost || '').trim() || EMAIL_DEFAULTS.smtpHost,
        smtpPort: Number(emailForm.smtpPort) || EMAIL_DEFAULTS.smtpPort,
        smtpSecure: !!emailForm.smtpSecure,
        smtpUser: String(emailForm.smtpUser || emailForm.fromEmail || '').trim(),
        replyTo: String(emailForm.replyTo || '').trim(),
        updatedAt: serverTimestamp(),
      };
      if (pwd) {
        payload.smtpPassword = pwd;
      } else if (prev.exists() && prev.data().smtpPassword) {
        payload.smtpPassword = prev.data().smtpPassword;
      }
      if (payload.enabled && (!payload.fromEmail || !payload.smtpPassword)) {
        throw new Error(t('emailSettingsMissing'));
      }
      await setDoc(ref, payload, { merge: true });
      setEmailForm((f) => ({ ...f, smtpPassword: '' }));
    } catch (e2) {
      setErr(e2.message || 'Error');
    } finally {
      setEmailSaving(false);
    }
  };

  const saveChatSettings = async () => {
    setErr('');
    setChatSaving(true);
    try {
      const h = Math.max(1, Math.min(168, Number(chatForm.unansweredAfterHours) || 24));
      await setDoc(
        doc(db, 'settings', 'chat'),
        { unansweredAfterHours: h, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (e2) {
      setErr(e2.message || 'Error');
    } finally {
      setChatSaving(false);
    }
  };

  const saveMarriageSettings = async (e) => {
    e.preventDefault();
    setErr('');
    setMarriageSaving(true);
    try {
      if (!marriageForm.sinceLocal) {
        throw new Error(t('marriageDateRequired'));
      }
      const since = Timestamp.fromDate(new Date(marriageForm.sinceLocal));
      await setDoc(
        doc(db, 'settings', 'marriage'),
        {
          title: String(marriageForm.title || '').trim(),
          since,
          visibleUserIds: marriageForm.visibleUserIds || [],
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (e2) {
      setErr(e2.message || 'Error');
    } finally {
      setMarriageSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setErr('');
    setEmailTestSending(true);
    try {
      const fn = httpsCallable(functions, 'adminSendTestEmail');
      await fn({});
    } catch (e2) {
      setErr(e2.message || 'Error');
    } finally {
      setEmailTestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="haydi-muted" role="status" aria-live="polite">…</div>
    );
  }

  return (
    <div>
      <h1 className="haydi-page-title">{t('adminSettings')}</h1>
      {err && <div className="haydi-alert" role="alert">{err}</div>}

      <div className="haydi-pill-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'users'}
          className={`haydi-pill-tab${view === 'users' ? ' haydi-pill-tab--active' : ''}`}
          onClick={() => setView('users')}
        >
          {t('adminUsers')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'email'}
          className={`haydi-pill-tab${view === 'email' ? ' haydi-pill-tab--active' : ''}`}
          onClick={() => setView('email')}
        >
          {t('emailNotifications')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'marriage'}
          className={`haydi-pill-tab${view === 'marriage' ? ' haydi-pill-tab--active' : ''}`}
          onClick={() => setView('marriage')}
        >
          {t('adminMarriage')}
        </button>
      </div>

      {view === 'users' && (
        <>
          <form className="haydi-card haydi-card--pad" onSubmit={handleCreateUser} style={{ marginBottom: '1.5rem' }}>
            <h2 className="haydi-section-title" style={{ marginBottom: '0.75rem' }}>{t('createUser')}</h2>
            <div className="haydi-field-grid">
              <input
                required
                className="haydi-input"
                placeholder={t('name')}
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              />
              <input
                required
                className="haydi-input"
                type="email"
                placeholder={t('email')}
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              />
              <input
                required
                className="haydi-input"
                type="password"
                placeholder={t('password')}
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
              />
              <input
                className="haydi-input"
                placeholder={t('phone')}
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
              />
              <input
                className="haydi-input"
                placeholder={t('avatarUrl')}
                value={createForm.avatarUrl}
                onChange={(e) => setCreateForm((f) => ({ ...f, avatarUrl: e.target.value }))}
              />
              <select
                className="haydi-select"
                value={createForm.role}
                onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
              >
                <option value="user">{t('roleUser')}</option>
                <option value="admin">{t('roleAdmin')}</option>
              </select>
            </div>

            <button type="submit" className="haydi-btn haydi-btn--primary" style={{ marginTop: '0.75rem' }}>
              {t('createUser')}
            </button>
          </form>

          <div className="haydi-table-wrap">
            <table className="haydi-table">
              <thead>
                <tr>
                  <th>{t('name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('uuid')}</th>
                  <th>{t('adminProfileScore')}</th>
                  <th>{t('role')}</th>
                  <th>{t('frozen')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: '0.75rem',
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    >
                      {u.publicId}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <input
                        type="number"
                        className="haydi-input haydi-input--inline"
                        style={{ width: 72, marginRight: 6 }}
                        value={scoreInputs[u._id] !== undefined ? scoreInputs[u._id] : (u.profileScore ?? 0)}
                        onChange={(e) => setScoreInputs((s) => ({ ...s, [u._id]: e.target.value }))}
                        aria-label={t('adminProfileScore')}
                      />
                      <button
                        type="button"
                        className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                        onClick={() => saveUserProfileScore(u)}
                      >
                        {t('adminSaveProfileScore')}
                      </button>
                    </td>
                    <td>{u.role}</td>
                    <td>{u.frozen ? t('yes') : t('no')}</td>
                    <td style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button
                        type="button"
                        className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                        onClick={() => toggleFreeze(u)}
                      >
                        {u.frozen ? t('unfreeze') : t('freeze')}
                      </button>
                      <button
                        type="button"
                        className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                        onClick={() => removeUser(u)}
                        disabled={u._id === user.uid}
                      >
                        {t('delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'email' && (
        <>
        <form
          onSubmit={saveEmailSettings}
          style={{
            padding: 16,
            background: '#f6fdff',
            borderRadius: 12,
            border: '1px solid var(--haydi-primary-faint)',
            maxWidth: 560,
          }}
        >
          <h3 style={{ color: 'var(--haydi-primary)', marginTop: 0 }}>{t('emailNotifications')}</h3>
          <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: 16 }}>
            {t('emailNotificationsHint')}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={emailForm.enabled}
              onChange={(e) => setEmailForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            {t('emailEnabled')}
          </label>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('emailFromAddress')}</div>
              <input
                type="email"
                required
                value={emailForm.fromEmail}
                onChange={(e) => setEmailForm((f) => ({ ...f, fromEmail: e.target.value }))}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('emailFromName')}</div>
              <input
                value={emailForm.fromName}
                onChange={(e) => setEmailForm((f) => ({ ...f, fromName: e.target.value }))}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('smtpHost')}</div>
              <input
                value={emailForm.smtpHost}
                onChange={(e) => setEmailForm((f) => ({ ...f, smtpHost: e.target.value }))}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('smtpPort')}</div>
                <input
                  type="number"
                  min={1}
                  max={65535}
                  value={emailForm.smtpPort}
                  onChange={(e) => setEmailForm((f) => ({ ...f, smtpPort: Number(e.target.value) }))}
                  style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={emailForm.smtpSecure}
                  onChange={(e) => setEmailForm((f) => ({ ...f, smtpSecure: e.target.checked }))}
                />
                {t('smtpTls')}
              </label>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('smtpUser')}</div>
              <input
                type="email"
                value={emailForm.smtpUser}
                onChange={(e) => setEmailForm((f) => ({ ...f, smtpUser: e.target.value }))}
                placeholder={emailForm.fromEmail}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('smtpAppPassword')}</div>
              <input
                type="password"
                autoComplete="new-password"
                value={emailForm.smtpPassword}
                onChange={(e) => setEmailForm((f) => ({ ...f, smtpPassword: e.target.value }))}
                placeholder={emailHasStoredPassword ? t('smtpPasswordPlaceholder') : ''}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
              />
              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 4 }}>{t('smtpAppPasswordHint')}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{t('emailReplyTo')}</div>
              <input
                type="email"
                value={emailForm.replyTo}
                onChange={(e) => setEmailForm((f) => ({ ...f, replyTo: e.target.value }))}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
            <button
              type="submit"
              disabled={emailSaving}
              style={{
                background: 'var(--haydi-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '0.5rem 1.2rem',
                fontWeight: 600,
                cursor: emailSaving ? 'wait' : 'pointer',
              }}
            >
              {emailSaving ? '…' : t('saveEmailSettings')}
            </button>
            <button
              type="button"
              onClick={sendTestEmail}
              disabled={emailTestSending}
              style={{
                background: '#e8f8fa',
                color: '#0a5',
                border: '1px solid var(--haydi-border)',
                borderRadius: 8,
                padding: '0.5rem 1.2rem',
                fontWeight: 600,
                cursor: emailTestSending ? 'wait' : 'pointer',
              }}
            >
              {emailTestSending ? '…' : t('sendTestEmail')}
            </button>
          </div>
        </form>
        <div
          style={{
            marginTop: 24,
            padding: 16,
            background: '#f6fdff',
            borderRadius: 12,
            border: '1px solid var(--haydi-primary-faint)',
            maxWidth: 560,
          }}
        >
          <h3 style={{ color: 'var(--haydi-primary)', marginTop: 0 }}>{t('adminChatReminderTitle')}</h3>
          <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: 12 }}>{t('adminChatReminderHint')}</p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, fontSize: '0.85rem' }}>
            {t('unansweredAfterHoursLabel')}
            <input
              type="number"
              min={1}
              max={168}
              value={chatForm.unansweredAfterHours}
              onChange={(e) =>
                setChatForm((f) => ({ ...f, unansweredAfterHours: Number(e.target.value) }))
              }
              style={{ maxWidth: 120, padding: '0.45rem', borderRadius: 8, border: '1px solid var(--haydi-border)' }}
            />
          </label>
          <button
            type="button"
            onClick={saveChatSettings}
            disabled={chatSaving}
            style={{
              marginTop: 14,
              background: 'var(--haydi-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '0.5rem 1.2rem',
              fontWeight: 600,
              cursor: chatSaving ? 'wait' : 'pointer',
            }}
          >
            {chatSaving ? '…' : t('saveChatSettings')}
          </button>
        </div>
        </>
      )}

      {view === 'marriage' && (
        <form className="haydi-card haydi-card--pad" onSubmit={saveMarriageSettings} style={{ maxWidth: 640 }}>
          <h2 className="haydi-section-title" style={{ marginTop: 0 }}>{t('adminMarriage')}</h2>
          <p className="haydi-muted" style={{ marginBottom: '1rem' }}>{t('adminMarriageHint')}</p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem', fontWeight: 600, fontSize: '0.875rem' }}>
            {t('marriageIntroLabel')}
            <input
              className="haydi-input"
              value={marriageForm.title}
              onChange={(e) => setMarriageForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('marriageIntroPlaceholder')}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem', fontWeight: 600, fontSize: '0.875rem' }}>
            {t('marriageDateLabel')}
            <input
              className="haydi-input"
              type="datetime-local"
              required
              value={marriageForm.sinceLocal}
              onChange={(e) => setMarriageForm((f) => ({ ...f, sinceLocal: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem', fontWeight: 600, fontSize: '0.875rem' }}>
            {t('marriageVisibleUsers')}
            <select
              multiple
              className="haydi-select haydi-input"
              size={Math.min(12, Math.max(4, users.length))}
              value={marriageForm.visibleUserIds}
              onChange={(e) =>
                setMarriageForm((f) => ({
                  ...f,
                  visibleUserIds: [...e.target.selectedOptions].map((o) => o.value),
                }))
              }
            >
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name || u.email || u._id}
                </option>
              ))}
            </select>
            <span className="haydi-muted" style={{ fontWeight: 400, fontSize: '0.8125rem' }}>{t('marriageVisibleHint')}</span>
          </label>
          <button type="submit" className="haydi-btn haydi-btn--primary" disabled={marriageSaving}>
            {marriageSaving ? '…' : t('marriageSave')}
          </button>
        </form>
      )}

    </div>
  );
}

export default AdminTab;
